import { useEffect, useRef, useState } from "react";
import type { ModeConfig } from "../state/modeConfig";
import type { usePlayerState } from "../hooks/usePlayerState";
import { useGameSession } from "../session/useGameSession";
import {
  generateSpatialSequence,
  gridSizeForDifficulty,
  scoreSpatial,
  sequenceLengthForDifficulty,
  type Position,
} from "../logic/spatial";
import { TopBar } from "../components/TopBar";
import { Instructions } from "../screens/Instructions";
import { Results } from "../screens/Results";
import { FeedbackOverlay } from "../components/FeedbackOverlay";
import { CountdownOverlay } from "../components/CountdownOverlay";
import { MODE_COUNTDOWN_MS } from "../state/modeConfig";
import { useTranslation } from "../i18n/useTranslation";

const CELL_SHOW_MS = 600;
const CELL_GAP_MS = 200;

type SubPhase = "presenting" | "recalling";

interface SpatialRecallProps {
  config: ModeConfig;
  player: ReturnType<typeof usePlayerState>;
  onExit: () => void;
}

export function SpatialRecall({ config, player, onExit }: SpatialRecallProps) {
  const game = useGameSession(config.modeId, player);
  const { session } = game;
  const { t } = useTranslation();

  const [sequence, setSequence] = useState<Position[]>([]);
  const [clicks, setClicks] = useState<Position[]>([]);
  const [subPhase, setSubPhase] = useState<SubPhase>("presenting");
  const [presentIndex, setPresentIndex] = useState(0);
  const [gridSize, setGridSize] = useState(4);
  const [recallSeconds, setRecallSeconds] = useState(0);

  const lastRoundRef = useRef(-1);
  useEffect(() => {
    if (session.phase.kind !== "playing") return;
    if (lastRoundRef.current === session.roundNum) return;
    lastRoundRef.current = session.roundNum;
    startRound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.phase, session.roundNum]);

  function startRound(): void {
    const gs = gridSizeForDifficulty(session.difficulty);
    const seqLen = sequenceLengthForDifficulty(session.difficulty);
    setGridSize(gs);
    setSequence(generateSpatialSequence(gs, seqLen));
    setClicks([]);
    setPresentIndex(0);
    setSubPhase("presenting");
    setRecallSeconds(Math.max(10, 25 - session.difficulty));
  }

  useEffect(() => {
    if (subPhase !== "presenting" || sequence.length === 0) return;
    if (presentIndex >= sequence.length) {
      setSubPhase("recalling");
      return;
    }
    const id = window.setTimeout(
      () => setPresentIndex((i) => i + 1),
      CELL_SHOW_MS + CELL_GAP_MS,
    );
    return () => window.clearTimeout(id);
  }, [subPhase, sequence, presentIndex]);

  useEffect(() => {
    if (subPhase !== "recalling") return;
    if (recallSeconds <= 0) {
      submit();
      return;
    }
    const id = window.setTimeout(() => setRecallSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPhase, recallSeconds]);

  function handleCellTap(row: number, col: number): void {
    if (subPhase !== "recalling" || clicks.length >= sequence.length) return;
    setClicks([...clicks, { row, col }]);
  }
  function undo(): void {
    if (subPhase !== "recalling" || clicks.length === 0) return;
    setClicks(clicks.slice(0, -1));
  }
  function submit(): void {
    if (subPhase !== "recalling") return;
    const result = scoreSpatial(clicks, sequence);
    game.submitAnswer(result.accuracy >= 0.7);
  }

  if (session.phase.kind === "instructions") {
    const gs = gridSizeForDifficulty(session.difficulty);
    const cells = sequenceLengthForDifficulty(session.difficulty);
    return (
      <Instructions
        config={config}
        difficulty={session.difficulty}
        highScore={session.playerState.modeHighScores[config.modeId] ?? 0}
        instructions={[
          t("spatial.instr.watch", { cells, grid: gs }),
          t("spatial.instr.tap"),
          t("spatial.instr.undo"),
          t("spatial.instr.threshold"),
        ]}
        difficultyDescription={t("spatial.diffDesc", { grid: gs, cells })}
        onStart={game.startPlaying}
        onBack={onExit}
        onDifficultyChange={game.setDifficulty}
      />
    );
  }
  if (session.phase.kind === "results") {
    return (
      <Results
        modeName={t(`mode.${config.modeId}.name`)}
        result={session.sessionResult}
        level={session.playerState.level}
        xpProgressFrac={session.playerState.xpProgressFrac}
        onContinue={onExit}
      />
    );
  }

  const currentHighlight =
    subPhase === "presenting" && presentIndex < sequence.length
      ? sequence[presentIndex]
      : null;

  const clickIndexFor = (row: number, col: number): number =>
    clicks.findIndex((p) => p.row === row && p.col === col);

  return (
    <main className="min-h-screen flex flex-col relative">
      <TopBar
        modeName={t(`mode.${config.modeId}.name`)}
        score={session.score}
        streak={session.streak.current}
        roundNumber={Math.min(session.roundNum + 1, session.maxRounds)}
        totalRounds={session.maxRounds}
      />

      <section className="flex-1 flex flex-col items-center justify-center px-6 gap-6 relative">
        <span className="font-mono text-xs tracking-[0.18em] uppercase text-teal">
          {subPhase === "presenting" ? t("spatial.watch") : t("spatial.tap")}
        </span>

        <div
          className="grid gap-1.5"
          style={{
            gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
            maxWidth: `min(${gridSize * 64}px, 90vw)`,
            width: "100%",
            aspectRatio: "1 / 1",
          }}
        >
          {Array.from({ length: gridSize * gridSize }, (_, idx) => {
            const row = Math.floor(idx / gridSize);
            const col = idx % gridSize;
            const isHighlight =
              currentHighlight !== null &&
              currentHighlight.row === row &&
              currentHighlight.col === col;
            const ci = clickIndexFor(row, col);
            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleCellTap(row, col)}
                className={[
                  "aspect-square rounded-lg border-2 flex items-center justify-center font-bold text-sm",
                  isHighlight
                    ? "bg-teal border-teal text-bg-1"
                    : ci >= 0
                      ? "bg-teal/40 border-teal text-fg-0"
                      : "bg-bg-2 border-[var(--color-line)]",
                ].join(" ")}
                aria-label={t("spatial.cellLabel", { row: row + 1, col: col + 1 })}
                disabled={subPhase !== "recalling"}
              >
                {ci >= 0 ? ci + 1 : ""}
              </button>
            );
          })}
        </div>

        {subPhase === "recalling" && (
          <>
            <div className="text-fg-3 font-mono text-xs">
              {clicks.length} / {sequence.length}
              {recallSeconds <= 5 && (
                <span className="text-red ml-3">
                  {t("common.hurry", { seconds: recallSeconds })}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={undo}
                disabled={clicks.length === 0}
                className="px-5 py-2.5 rounded-full bg-bg-2 text-fg-2 text-sm font-medium disabled:opacity-40"
              >
                {t("common.undo")}
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={clicks.length === 0}
                className="px-7 py-2.5 rounded-full bg-teal text-bg-1 text-sm font-semibold disabled:opacity-40"
              >
                {t("common.submit")}
              </button>
            </div>
          </>
        )}

        {session.phase.kind === "countdown" && (
          <CountdownOverlay
            durationMs={MODE_COUNTDOWN_MS[config.modeId] ?? 0}
            onSkip={game.finishCountdown}
          />
        )}

        {session.phase.kind === "feedback" && (
          <FeedbackOverlay correct={session.phase.correct} />
        )}
      </section>
    </main>
  );
}
